import React from 'react';
import { Alert, Platform, Pressable, RefreshControl, ScrollView, View } from 'react-native';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useRoute } from '@react-navigation/native';

import api from '../../../utils/api';
import tw from '../../../styles/tailwind';
import Text from '../../../components/Text';
import Toggle from '../../../components/Toggle';

type CommunityDetail = {
  community: {
    uuid: string;
    name: string;
    units_count?: number;
    residents_count?: number;
    access_control_panels_count?: number;
    is_test?: boolean;
  };
  settings: Record<string, any>;
  editable_settings_keys: string[];
  features: Record<string, { enabled: boolean; trial_until: string | null; active: boolean }>;
};

const FEATURE_LABELS: Record<string, string> = {
  argos: 'Cámaras (Argos)',
  vinte: 'Vinte',
};

const TRIAL_DAYS = 15;

function trialUntilFromToday(days: number): string {
  const date = new Date();
  date.setDate(date.getDate() + days);
  return date.toISOString().slice(0, 10);
}

const SETTING_LABELS: Record<string, string> = {
  'requires_accept_tos': 'Requiere aceptar términos',
  'guest_passes.resident_can_create': 'Residentes pueden crear pases',
  'tickets.enabled': 'Incidencias activadas',
  'fees.enabled': 'Cuotas activadas',
  'tags.enabled': 'Tags de acceso activados',
  'access_authorization.enabled': 'Autorizaciones de acceso activadas',
};

function getSetting(settings: Record<string, any>, path: string): boolean {
  return path.split('.').reduce((acc, key) => (acc == null ? acc : acc[key]), settings) === true;
}

export default function CommunityDetailScreen() {
  const route = useRoute<any>();
  const uuid = route.params?.uuid;
  const qc = useQueryClient();

  const { data, isLoading, refetch } = useQuery<CommunityDetail>({
    queryKey: ['meta.community', uuid],
    queryFn: () => api.get(`/admin/communities/${uuid}`).then(r => r.data),
  });

  const onError = (e: any) => {
    const msg = e?.response?.data?.message ?? 'No se pudo actualizar';
    if (Platform.OS === 'web') window.alert(msg);
    else Alert.alert('Error', msg);
  };

  const featureMutation = useMutation({
    mutationFn: ({ feature, ...body }: { feature: string; enabled: boolean; trial_until?: string | null }) =>
      api.patch(`/admin/communities/${uuid}/features/${feature}`, body).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['meta.community', uuid] }),
    onError,
  });

  const communityMutation = useMutation({
    mutationFn: (patch: { is_test: boolean }) =>
      api.patch(`/admin/communities/${uuid}`, patch).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['meta.community', uuid] }),
    onError,
  });

  const mutation = useMutation({
    mutationFn: (patch: Record<string, boolean>) =>
      api.patch(`/admin/communities/${uuid}/settings`, patch).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['meta.community', uuid] }),
    onError,
  });

  return (
    <SafeAreaView style={tw`flex-1 bg-background`}>
      <ScrollView
        contentContainerStyle={tw`p-4`}
        refreshControl={
          Platform.OS === 'web' ? undefined : (
            <RefreshControl onRefresh={refetch} refreshing={isLoading} />
          )
        }
      >
        <View style={tw`bg-white rounded-lg p-4 mb-4`}>
          <Text style={tw`text-xl font-600 text-gray-800`}>{data?.community.name}</Text>
          <Text style={tw`text-gray-500 text-sm mt-1`}>
            {data?.community.units_count ?? 0} viviendas ·{' '}
            {data?.community.residents_count ?? 0} residentes ·{' '}
            {data?.community.access_control_panels_count ?? 0} paneles
          </Text>
        </View>

        <View style={tw`bg-white rounded px-4 py-3 mb-4 row justify-between items-center`}>
          <Text style={tw`flex-1 text-gray-700`}>Comunidad de prueba</Text>
          <Toggle
            value={!!data?.community.is_test}
            onValueChange={v => communityMutation.mutate({ is_test: v })}
            disabled={communityMutation.isPending}
          />
        </View>

        <Text style={tw`text-gray-700 font-600 mb-2`}>Módulos contratados</Text>

        {Object.entries(data?.features ?? {}).map(([feature, config]) => (
          <View key={feature} style={tw`bg-white rounded px-4 py-3 mb-2`}>
            <View style={tw`row justify-between items-center`}>
              <Text style={tw`flex-1 text-gray-700`}>{FEATURE_LABELS[feature] ?? feature}</Text>
              <Toggle
                value={config.enabled}
                onValueChange={v => featureMutation.mutate({ feature, enabled: v })}
                disabled={featureMutation.isPending}
              />
            </View>

            {config.enabled && (
              <View style={tw`row justify-between items-center mt-2`}>
                <Text style={tw`text-sm ${config.active ? 'text-gray-500' : 'text-red'}`}>
                  {!config.trial_until
                    ? 'Sin vencimiento'
                    : config.active
                      ? `Prueba hasta ${config.trial_until}`
                      : `Prueba vencida el ${config.trial_until}`}
                </Text>
                <Pressable
                  disabled={featureMutation.isPending}
                  onPress={() => featureMutation.mutate({
                    feature,
                    enabled: true,
                    trial_until: config.trial_until ? null : trialUntilFromToday(TRIAL_DAYS),
                  })}
                >
                  <Text style={tw`text-sm text-secondary font-600`}>
                    {config.trial_until ? 'Quitar vencimiento' : `Prueba ${TRIAL_DAYS} días`}
                  </Text>
                </Pressable>
              </View>
            )}
          </View>
        ))}

        <Text style={tw`text-gray-700 font-600 mt-4 mb-2`}>Funciones</Text>

        {(data?.editable_settings_keys ?? []).map(key => (
          <View
            key={key}
            style={tw`bg-white rounded px-4 py-3 mb-2 row justify-between items-center`}
          >
            <Text style={tw`flex-1 text-gray-700`}>{SETTING_LABELS[key] ?? key}</Text>
            <Toggle
              value={getSetting(data?.settings ?? {}, key)}
              onValueChange={v => mutation.mutate({ [key]: v })}
              disabled={mutation.isPending}
            />
          </View>
        ))}
      </ScrollView>
    </SafeAreaView>
  );
}
